import { Auth0Client, createAuth0Client } from '@auth0/auth0-spa-js';
import { AuthenticationHandler, AuthenticationResult, AuthenticationConfig } from './types';
import { appInsights } from '../../../utils/appInsights';

class WebAuthHandler implements AuthenticationHandler {
  private auth0Client?: Auth0Client;

  private config: AuthenticationConfig;

  constructor(config: AuthenticationConfig) {
    this.config = config;
  }

  async initialize(): Promise<AuthenticationResult> {
    try {
      appInsights.trackEvent({
        name: '[Auth] Initializing Web Auth0Client',
        properties: { mode: 'web' }
      });

      this.auth0Client = await createAuth0Client({
        domain: this.config.domain,
        clientId: this.config.clientId,
        authorizationParams: {
          redirect_uri: this.config.redirectUri || window.location.origin,
          audience: this.config.audience
        },
        cacheLocation: 'localstorage',
        useRefreshTokens: true
      });

      const query = window.location.search;
      const shouldHandleRedirect = query.includes('code=') && query.includes('state=');

      if (shouldHandleRedirect) {
        await this.auth0Client.handleRedirectCallback();
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      try {
        await this.auth0Client.getTokenSilently();

        const searchParams = new URLSearchParams(document.location.search);
        if (searchParams.get('error_description') === 'User cancelled') {
          window.history.replaceState({}, document.title, window.location.pathname);
        }

        return {
          isAuthenticated: true,
          auth0Client: this.auth0Client
        };
      } catch (error) {
        const searchParams = new URLSearchParams(window.location.search);
        const isAccessDenied = searchParams.get('error') === 'access_denied';

        if (!isAccessDenied) {
          const authParams = {
            authorizationParams: {
              ui_locales: sessionStorage.getItem('ui_locales') || ''
            }
          };

          if (searchParams.get('switch_language')) {
            const newLanguage = searchParams.get('switch_language') || '';
            sessionStorage.setItem('ui_locales', newLanguage);
            authParams.authorizationParams = {
              ui_locales: newLanguage
            };
          }

          this.auth0Client.loginWithRedirect(authParams);
          appInsights.trackEvent({
            name: 'LoginWithRedirect',
            properties: { mode: 'web' }
          });
        } else if (searchParams.get('error_description') === 'User cancelled') {
          this.auth0Client.loginWithRedirect();
        }

        return {
          isAuthenticated: false,
          isAccessDenied,
          auth0Client: this.auth0Client
        };
      }
    } catch (error) {
      appInsights.trackException({
        exception: error instanceof Error ? error : new Error(String(error))
      });

      return {
        isAuthenticated: false,
        auth0Client: this.auth0Client
      };
    }
  }

  async refresh(): Promise<string | null> {
    if (!this.auth0Client) {
      return null;
    }

    try {
      return await this.auth0Client.getTokenSilently();
    } catch (error) {
      appInsights.trackException({
        exception: error instanceof Error ? error : new Error(String(error))
      });
      return null;
    }
  }
}

export default WebAuthHandler;
